import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useLang } from "../contexts/LanguageContext";
import QuickReplies from "./DanaQuickReplies";
import { Send, Sparkles, X, Mic, Loader2, Trash2, Play, Pause, Lock } from "lucide-react";

// Dana lives ONLY as a bottom-right floating chat popover.
// The old inline "Meet Dana" hero-style section (title + subtext + huge
// portrait + status bar) has been removed — the site showcases experiences
// as the product, not Dana's face.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DANA_AVATAR = "https://customer-assets.emergentagent.com/wingman/4d88747a-9285-4415-a858-393ef890ce1a/attachments/15204f80bb5a4f1087b4962c549d4d64_image.bin";

function makeId() {
  return "sess_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export default function Dana() {
  const { t, isAr, lang } = useLang();
  const [sessionId, setSessionId] = useState(() => {
    let s = localStorage.getItem("dana_session");
    if (!s) { s = makeId(); localStorage.setItem("dana_session", s); }
    return s;
  });
  // Segment context ("explorer" | "business" | "teams" | null) — set when the
  // user clicks one of the "Request Now" cards. Backend uses it to drive the
  // per-segment qualifying flow (3-4 questions) BEFORE asking for name+phone.
  const [flowContext, setFlowContext] = useState(null);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: t("dana_intro") },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // ── Voice recording — WhatsApp-style press-and-hold ────────────────
  //   Hold mic → records. Release → captures blob & shows a voice bubble
  //   preview in the input row (with duration + trash + arrow-send).
  //   Click send → the blob is uploaded to /api/transcribe-audio, the
  //   transcribed text is fed straight to Dana as if the user typed it,
  //   and a voice bubble is added to the chat history (user never sees
  //   the transcribed text in the input).
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [voiceSending, setVoiceSending] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const autoStopRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  // Track pointer/touch start X so we can detect the ONLY gesture we
  // support: slide LEFT > 80 px while holding → cancel and discard.
  const pointerStartXRef = useRef(0);
  // iOS-safe: prefer native touch events over pointer events for the mic
  // gesture. When touchstart fires, `touchModeRef` blocks the synthesized
  // pointer events that iOS emits afterwards from re-triggering the same
  // logic (which would corrupt refs mid-gesture).
  const touchModeRef = useRef(false);
  const cancelledRef = useRef(false);     // set true when the user slide-cancels mid-hold
  const shouldAutoSendRef = useRef(false); // set true on release so recorder.onstop auto-sends
  const MAX_MIC_MS = 60_000;
  const CANCEL_THRESHOLD_PX = 80;         // LEFT-swipe cancel threshold (the only gesture)
  const MIC_ERROR_MSG = isAr ? "تعذّر التسجيل، اكتب رسالتك مباشرةً" : "Recording failed, please type instead";

  const cleanupMicStream = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    streamRef.current = null;
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  // ONE place to fully reset every piece of voice-recording state.
  // Called at the start of every new recording AND at every exit path
  // (success, cancel, error). Guarantees that attempt N+1 behaves identically
  // to attempt 1: fresh MediaRecorder, fresh stream, cleared timers, empty
  // chunks, cleared handler references, cleared pointer-tracking refs.
  const resetRecordingState = () => {
    console.log("[Dana Voice] resetRecordingState() — full wipe");
    // 1. Stop timers & clear pending auto-stop.
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    // 2. Detach MediaRecorder handlers so any late `onstop` from the OLD
    //    instance can no longer fire our React setters or send() logic.
    if (recorderRef.current) {
      try {
        recorderRef.current.ondataavailable = null;
        recorderRef.current.onstop = null;
        recorderRef.current.onerror = null;
        if (recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        }
      } catch (_) {}
      recorderRef.current = null;
    }
    // 3. Kill the mic stream tracks so the browser stops the "mic in use" indicator.
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    streamRef.current = null;
    // 4. Zero all working data.
    chunksRef.current = [];
    pointerStartXRef.current = 0;
    cancelledRef.current = false;
    shouldAutoSendRef.current = false;
    startedAtRef.current = 0;
    // 5. Reset React state (batched by React 18+ so this is a single render).
    setRecording(false);
    setElapsed(0);
  };

  useEffect(() => cleanupMicStream, []);

  // When the Dana panel closes, aggressively tear down any active recording
  // state — otherwise a background MediaRecorder could keep the iOS mic
  // indicator lit and cause the NEXT time the panel opens to fail on the
  // first recording. resetRecordingState() stops tracks, nulls handlers,
  // clears chunks & timers.
  useEffect(() => {
    if (open) return;
    resetRecordingState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Start recording — called from the mic button's pointerdown handler.
  // Every call runs `resetRecordingState()` FIRST so any leftover state
  // from a previous session (dangling MediaRecorder, unclosed tracks,
  // stale chunks, stale event handlers) is nuked before we open a new
  // mic stream. This is the fix for "voice works only for the first
  // message" — iOS Safari's mic-in-use indicator was staying lit because
  // the previous stream wasn't torn down cleanly between recordings.
  const startRecording = async () => {
    console.log("[Dana Voice] press → startRecording");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setVoiceError(MIC_ERROR_MSG);
      setTimeout(() => setVoiceError(""), 3000);
      return;
    }
    // Full wipe before every new attempt — no state can leak from the previous recording.
    resetRecordingState();
    setVoiceError("");
    try {
      // Rich constraints: echo cancel + noise suppress + Whisper-native
      // 16 kHz sample rate. iOS may ignore some hints but always accepts
      // this shape without throwing.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
        },
      });
      streamRef.current = stream;
      chunksRef.current = [];
      let mimeType = "";
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus", ""];
      for (const t of candidates) {
        if (t === "" || (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t))) { mimeType = t; break; }
      }
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onerror = (e) => console.error("[Dana Voice] recorder.onerror:", e);

      recorder.onstop = () => {
        console.log("[Dana Voice] onstop, chunks=", chunksRef.current.length,
                    "autoSend=", shouldAutoSendRef.current);
        cleanupMicStream();
        setRecording(false);
        const finalSec = (Date.now() - startedAtRef.current) / 1000;
        if (!chunksRef.current.length) {
          shouldAutoSendRef.current = false;
          setVoiceError(MIC_ERROR_MSG);
          setTimeout(() => setVoiceError(""), 3000);
          return;
        }
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        chunksRef.current = [];
        // MINIMUM RECORDING GUARDS — reject clips too short for Whisper to
        // reliably transcribe. Either duration < 1.5 s OR blob < 1000 bytes
        // triggers a friendly hint instead of a wasted API call.
        const MIN_DURATION_SEC = 1.5;
        const MIN_BLOB_BYTES = 1000;
        if (finalSec < MIN_DURATION_SEC || blob.size < MIN_BLOB_BYTES) {
          console.warn("[Dana Voice] clip too short — sec:", finalSec, "bytes:", blob.size);
          shouldAutoSendRef.current = false;
          setElapsed(0);
          const hint = isAr ? "اضغط أطول شوية" : "Hold a bit longer to record";
          setVoiceError(hint);
          setTimeout(() => setVoiceError(""), 2500);
          return;
        }
        // Release → auto-send (PTT flow). Cancelled recordings never reach
        // here because `discardRecording()` nulls the onstop handler first.
        const shouldSend = shouldAutoSendRef.current;
        shouldAutoSendRef.current = false;
        if (shouldSend) {
          sendVoiceBlob(blob, Math.max(1, Math.round(finalSec)));
        }
      };

      // 100 ms timeslice — faster chunk delivery so `onstop` has all
      // data ready immediately after a short release.
      recorder.start(100);
      startedAtRef.current = Date.now();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
      autoStopRef.current = setTimeout(() => {
        if (recorderRef.current?.state === "recording") recorderRef.current.stop();
      }, MAX_MIC_MS);
    } catch (err) {
      console.error("[Dana Voice] getUserMedia failed:", err);
      cleanupMicStream();
      setRecording(false);
      setVoiceError(MIC_ERROR_MSG);
      setTimeout(() => setVoiceError(""), 3000);
    }
  };

  // Stop recording — called from the mic button's pointerup/leave handler.
  const stopRecording = () => {
    console.log("[Dana Voice] release → stopRecording");
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch (err) {
      console.error("[Dana Voice] stop() threw:", err);
      cleanupMicStream();
      setRecording(false);
    }
  };

  // Silently discard the current recording — no send, no API call. Detaches
  // the onstop handler BEFORE stopping so the recorder can't still trigger
  // a send. Fires on left-swipe cancel and on panel close mid-recording.
  const discardRecording = () => {
    shouldAutoSendRef.current = false;
    cancelledRef.current = true;
    try {
      if (recorderRef.current) {
        recorderRef.current.onstop = null;
        recorderRef.current.ondataavailable = null;
        if (recorderRef.current.state !== "inactive") recorderRef.current.stop();
      }
    } catch (_) {}
    cleanupMicStream();
    chunksRef.current = [];
    setRecording(false);
    setElapsed(0);
    setVoiceError("");
  };

  // Send a voice blob straight to Dana. Called from recorder.onstop after
  // either an unlocked-release OR a locked-send tap.
  //
  // Whisper natively supports iOS Safari's `audio/mp4` (AAC) output, plus
  // webm/ogg/wav/mp3. The only requirement is that the FILENAME extension
  // matches the container so Whisper can pick the right decoder — some
  // proxies/CDNs strip Content-Type but always preserve the filename.
  const filenameForBlob = (blob) => {
    const t = (blob?.type || "").toLowerCase();
    if (t.includes("mp4") || t.includes("m4a") || t.includes("aac")) return "voice.m4a";
    if (t.includes("ogg"))                                            return "voice.ogg";
    if (t.includes("wav"))                                            return "voice.wav";
    if (t.includes("mpeg") || t.includes("mp3"))                      return "voice.mp3";
    return "voice.webm";
  };

  const sendVoiceBlob = async (blob, durationSec) => {
    if (voiceSending || loading) return;
    const uploadName = filenameForBlob(blob);
    console.log("[Dana Voice] sendVoiceBlob — size:", blob.size, "type:", blob.type,
                "filename:", uploadName, "duration:", durationSec, "s");
    setElapsed(0);
    setVoiceSending(true);
    // Create a local object URL so the user can play back their voice
    // message inside the chat bubble.
    let audioUrl = null;
    try { audioUrl = URL.createObjectURL(blob); } catch (_) {}
    setMessages((m) => [...m, { role: "user", isVoice: true, duration: durationSec, audioUrl }]);
    setLoading(true);

    // Wrap in File so Safari preserves the extension end-to-end.
    const audioFile = (typeof File !== "undefined")
      ? new File([blob], uploadName, { type: blob.type || "audio/mp4" })
      : blob;

    const attemptTranscribe = async (attempt) => {
      const fd = new FormData();
      fd.append("file", audioFile, uploadName);
      fd.append("model", "whisper-1");
      console.log(`[Dana Voice] POST /api/transcribe-audio (attempt ${attempt}, name=${uploadName}, type=${audioFile.type}, size=${audioFile.size})`);
      return axios.post(`${API}/transcribe-audio`, fd, { timeout: 90_000 });
    };

    // Wraps attemptTranscribe so BOTH network/HTTP errors AND empty-text
    // responses share a single retry path with a 1s backoff, and always
    // resolves to a friendly outcome — never re-throws to the caller.
    const friendlyEmpty = isAr
      ? "ما سمعتك زين، ممكن تعيد أو تكتب؟"
      : "I didn't quite catch that — could you say it again, or type your answer?";

    const runTranscription = async () => {
      // Optimistic try → on empty OR throw, wait 1s and try once more.
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          const res = await attemptTranscribe(attempt);
          const text = (res.data?.text || "").trim();
          const berr = (res.data?.error || "").trim();
          if (text) return { ok: true, text };
          console.warn(`[Dana Voice] attempt ${attempt} returned empty text. backendErr:`, berr);
          if (attempt === 1) {
            const processingMsg = isAr ? "جاري المعالجة..." : "Processing...";
            setMessages((m) => [...m, { role: "assistant", content: processingMsg, _transient: true }]);
            await new Promise((r) => setTimeout(r, 1000)); // 1 s backoff before retry
            setMessages((m) => m.filter((x) => !x._transient));
          }
        } catch (err) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail || err?.response?.data?.error || err?.message || "unknown";
          console.error(`[Dana Voice] attempt ${attempt} threw. status=${status} detail=${detail} type=${blob?.type} size=${audioFile.size}`);
          if (attempt === 1) {
            const processingMsg = isAr ? "جاري المعالجة..." : "Processing...";
            setMessages((m) => [...m, { role: "assistant", content: processingMsg, _transient: true }]);
            await new Promise((r) => setTimeout(r, 1000));
            setMessages((m) => m.filter((x) => !x._transient));
            continue;
          }
          // 2nd attempt also threw — give up with the friendly message.
          return { ok: false };
        }
      }
      return { ok: false };
    };

    try {
      const result = await runTranscription();
      if (!result.ok) {
        // Clean up any lingering transient bubbles.
        setMessages((m) => m.filter((x) => !x._transient));
        setMessages((m) => [...m, { role: "assistant", content: friendlyEmpty }]);
        return;
      }
      console.log("[Dana Voice] transcribed OK:", result.text.slice(0, 80));
      const cRes = await axios.post(`${API}/dana/chat`, {
        session_id: sessionId,
        message: result.text,
        lang,
        context: flowContext,
      });
      setMessages((m) => [...m, { role: "assistant", content: cRes.data.reply, options: cRes.data.options || [] }]);
    } catch (err) {
      // Only /api/dana/chat can reach here now — transcription is fully guarded.
      const status = err?.response?.status;
      console.error("[Dana Voice] dana/chat failed. status=", status, "err=", err);
      setMessages((m) => m.filter((x) => !x._transient));
      setMessages((m) => [...m, { role: "assistant", content: friendlyEmpty }]);
    } finally {
      setLoading(false);
      setVoiceSending(false);
      // Belt-and-suspenders: guarantee the mic is FULLY released after every
      // transcription attempt (success or failure). Without this, iOS Safari
      // can leave the mic-in-use indicator lit and the NEXT recording will
      // silently produce empty chunks.
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
      streamRef.current = null;
    }
  };

  // ── Simple PTT (push-to-talk) gesture handlers ───────────────────
  // Hold to record. Release to auto-send. The ONLY other gesture is a
  // LEFT-swipe of >80 px while holding, which cancels and discards.
  //
  // We use native touch events as the PRIMARY input on mobile because
  // iOS Safari's pointer events can be cancelled mid-vertical-drag by
  // the OS. `touchModeRef` blocks the synthesized pointer events iOS
  // fires after touchend so the desktop path can't double-trigger.

  // Positive value = swipe toward the cancel side (LEFT in LTR, RIGHT
  // in RTL so the gesture stays natural for the thumb in both dirs).
  const cancelDelta = (clientX) => {
    const raw = pointerStartXRef.current - clientX;
    return isAr ? -raw : raw;
  };

  const handleMove = (clientX) => {
    if (!recording || cancelledRef.current) return;
    if (cancelDelta(clientX) >= CANCEL_THRESHOLD_PX) {
      console.log("[Dana Voice] left-swipe > 80 px → CANCEL");
      cancelledRef.current = true;
      discardRecording();
      // Brief "Cancelled" hint so the user knows the recording was discarded.
      const msg = isAr ? "تم الإلغاء" : "Cancelled";
      setVoiceError(msg);
      setTimeout(() => setVoiceError(""), 1500);
    }
  };

  // Native touch handlers (mobile — iOS + Android).
  const onMicTouchStart = (e) => {
    touchModeRef.current = true;
    const t = e.touches[0];
    if (!t) return;
    pointerStartXRef.current = t.clientX;
    cancelledRef.current = false;
    startRecording();
  };
  const onMicTouchMove = (e) => {
    const t = e.touches[0];
    if (t) handleMove(t.clientX);
  };
  const onMicTouchEnd = () => {
    const clearTouchMode = () => { touchModeRef.current = false; };
    if (cancelledRef.current) { setTimeout(clearTouchMode, 250); return; }
    if (recording) {
      shouldAutoSendRef.current = true;
      stopRecording();
    }
    setTimeout(clearTouchMode, 250);
  };

  // Pointer handlers (desktop / mouse only).
  const onMicDown = (e) => {
    if (touchModeRef.current) return;
    e.preventDefault();
    pointerStartXRef.current = e.clientX ?? 0;
    cancelledRef.current = false;
    startRecording();
  };
  const onMicMove = (e) => {
    if (touchModeRef.current) return;
    handleMove(e.clientX ?? 0);
  };
  const onMicRelease = () => {
    if (touchModeRef.current) return;
    if (cancelledRef.current) return;
    if (recording) {
      shouldAutoSendRef.current = true;
      stopRecording();
    }
  };

  // Format a seconds count as m:ss (WhatsApp-style).
  const fmtDur = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  // Global opener — any component (Hero pill, Navbar link, Footer link, About cards)
  // triggers the popover with: window.dispatchEvent(new CustomEvent('tripex:open-dana', {detail:{opener, context}})).
  // When `detail.context` is provided (from About "Request Now" cards), we start
  // a FRESH conversation (new session_id, cleared history) so Dana's per-segment
  // qualifying flow runs cleanly from step 1 every time. The `context` is then
  // sent along with every subsequent /api/dana/chat call so the backend embeds
  // the correct segment-specific flow into her system prompt.
  useEffect(() => {
    const openHandler = (e) => {
      const opener = e?.detail?.opener;
      const context = e?.detail?.context || null;
      setOpen(true);
      if (context) {
        // Fresh flow — new session, cleared history, context locked.
        const newSess = makeId();
        localStorage.setItem("dana_session", newSess);
        setSessionId(newSess);
        setFlowContext(context);
        setMessages([{ role: "assistant", content: opener || t("dana_intro") }]);
        return;
      }
      if (opener) {
        setMessages((prev) => {
          if (prev.length <= 1 && prev[0]?.role === "assistant") {
            return [{ role: "assistant", content: opener }];
          }
          return [...prev, { role: "assistant", content: opener }];
        });
      }
    };
    window.addEventListener("tripex:open-dana", openHandler);
    return () => window.removeEventListener("tripex:open-dana", openHandler);
  }, [t]);

  // Refresh greeting when the language toggle flips (only if the chat hasn't started yet).
  useEffect(() => {
    setMessages((m) => {
      if (m.length === 1 && m[0].role === "assistant") return [{ role: "assistant", content: t("dana_intro") }];
      return m;
    });
    // eslint-disable-next-line
  }, [lang]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  // ── Mobile keyboard fix (iOS Safari-safe) ─────────────────────────
  // Approach: `interactive-widget=resizes-visual` in the viewport meta tells
  // Safari the keyboard is an overlay only — the layout viewport does NOT
  // shrink when the keyboard appears. Combined with a full-screen fixed
  // panel and a scroll-locked body, the chat frame stays rock-solid and
  // the keyboard simply overlays the input bar (which is at the flex
  // bottom of the panel and remains focusable).
  //
  // We only need to LOCK BODY SCROLL while the panel is open on mobile so
  // the underlying page can't scroll behind the sheet. No visualViewport
  // listener, no imperative transforms — the previous approaches caused
  // the "panel flies to top" bug because the layout viewport WAS shrinking
  // and our transform math compounded with iOS Safari's own repositioning.
  useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 768) return; // desktop → no scroll-lock needed

    // Capture current scroll to restore on close.
    const scrollY = window.scrollY;
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      width: document.body.style.width,
      top: document.body.style.top,
    };
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = `-${scrollY}px`;

    return () => {
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.width = prev.width;
      document.body.style.top = prev.top;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/dana/chat`, {
        session_id: sessionId,
        message: msg,
        lang,
        context: flowContext,
      });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, options: r.data.options || [] }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: isAr ? "حصل خطأ بسيط، جرّب مرة ثانية." : "Small hiccup — try again." },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, lang, isAr, flowContext]);

  return (
    <>
      {/* Trigger button — Dana's small circular avatar, bottom-right (or bottom-left in RTL) */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="dana-fab"
          aria-label={isAr ? "افتح دردشة دانة" : "Open Dana chat"}
          className={`fixed bottom-20 ${isAr ? 'left-5' : 'right-5'} z-40 group`}
          style={{ transition: 'transform .2s ease' }}
        >
          <span
            className="relative inline-flex items-center justify-center rounded-full shadow-lg hover:scale-105 active:scale-95 transition-transform duration-300"
            style={{
              width: 56,
              height: 56,
              background: '#FFFFFF',
              boxShadow: '0 8px 22px -6px rgba(15,58,54,0.35), 0 4px 10px rgba(0,0,0,0.15)',
              border: '2px solid rgba(15,58,54,0.15)',
            }}
          >
            <img
              src={DANA_AVATAR}
              alt="Dana"
              className="w-full h-full rounded-full object-cover"
            />
            <span
              className="absolute -top-0.5 -right-0.5 inline-flex h-3 w-3 rounded-full"
              style={{
                background: '#22C55E',
                boxShadow: '0 0 0 2px #FFFFFF, 0 0 8px rgba(34,197,94,0.7)',
              }}
              aria-hidden
            />
          </span>
        </button>
      )}

      {/* Chat popover panel */}
      {open && (
        <>
          {/* Mobile-only full-screen sheet CSS (uses !important to defeat
              Tailwind's floating popover classes). Above 768px the popover
              geometry from the className is preserved. */}
          <style>{`
            @media (max-width: 767px) {
              #dana-chat-container {
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                width: 100% !important;
                height: 100% !important;
                max-width: 100% !important;
                max-height: 100% !important;
                margin: 0 !important;
                border-radius: 0 !important;
                overflow: hidden !important;
                display: flex !important;
                flex-direction: column !important;
              }
              #dana-chat-container #dana-messages {
                flex: 1 1 auto !important;
                min-height: 0 !important;
                overflow-y: auto !important;
                -webkit-overflow-scrolling: touch !important;
              }
              #dana-chat-container > .dana-header,
              #dana-chat-container > #dana-input-bar {
                flex-shrink: 0 !important;
              }
              #dana-chat-container > #dana-input-bar {
                position: relative !important;
              }
            }
          `}</style>
          <div
            id="dana-chat-container"
            data-testid="dana-chat-panel"
            className={`fixed z-50 bottom-5 ${isAr ? 'left-5 right-5 md:right-auto md:left-5' : 'right-5 left-5 md:left-auto md:right-5'} md:w-[380px] md:max-w-[92vw] md:max-h-[80vh] flex flex-col rounded-2xl overflow-hidden`}
            style={{
              background: 'rgba(255,255,255,0.96)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              border: '1px solid rgba(15,58,54,0.15)',
              boxShadow: '0 24px 60px -12px rgba(15,58,54,0.35), 0 10px 24px rgba(0,0,0,0.12)',
              touchAction: 'manipulation',
              overscrollBehavior: 'contain',
            }}
          >
          {/* Header */}
          <div className="dana-header flex items-center gap-3 px-4 py-3 border-b border-sage/30">
            <img src={DANA_AVATAR} alt="" className="w-10 h-10 rounded-full object-cover border border-sage/40" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-emerald-deep font-cairo truncate"
                   style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}>
                {isAr ? "دانة" : "Dana"}
              </div>
              <div className="text-xs text-emerald-deep/60 font-cairo flex items-center gap-1.5">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ background: '#22C55E', boxShadow: '0 0 6px rgba(34,197,94,0.65)' }}
                />
                {isAr ? "متصلة الآن" : "Online now"}
              </div>
            </div>
            <Sparkles className="text-gold-deep" size={16} />
            <button
              onClick={() => setOpen(false)}
              data-testid="dana-close-btn"
              aria-label={isAr ? "أغلق" : "Close"}
              className="rounded-full w-8 h-8 flex items-center justify-center hover:bg-sage/20 transition"
            >
              <X size={18} className="text-emerald-deep" />
            </button>
          </div>

          {/* Messages — flex-1 fills the panel; scrolls internally. Padding
              bottom is imperatively grown by the keyboard-fix effect so the
              latest message stays visible above the translated input bar. */}
          <div
            ref={scrollRef}
            id="dana-messages"
            className="flex-1 overflow-y-auto scroll-styled space-y-3 px-3 py-3"
            style={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              minHeight: 0,
            }}
            data-testid="dana-messages"
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2 text-sm leading-relaxed font-cairo whitespace-pre-wrap ${m.role === "user" ? "bubble-user" : "bubble-bot"} ${m.isVoice ? "bubble-voice" : ""}`}
                  style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
                  data-testid={`dana-msg-${m.role}-${i}${m.isVoice ? "-voice" : ""}`}
                >
                  {m.isVoice ? (
                    <VoiceMessageBubble audioUrl={m.audioUrl} duration={m.duration || 0} fmtDur={fmtDur} />
                  ) : (
                    m.content
                  )}
                </div>
                {m.options?.length > 0 && i === messages.length - 1 && !loading && (
                  <QuickReplies options={m.options} onPick={send} isAr={isAr} />
                )}
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bubble-bot px-4 py-2.5 text-sm flex items-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
          </div>

          {/* Input row — 3 modes: locked (recording continues, released finger) > recording (holding) > idle */}
          <div
            id="dana-input-bar"
            className="px-3 py-3 border-t border-sage/30 bg-cream-light/60"
          >
            {recording ? (
              /* RECORDING — simple PTT: pulsing red mic + live timer + subtle
                 "slide left to cancel" hint. Release the mic to auto-send. */
              <div className="relative flex items-center gap-2" data-testid="dana-recording-row">
                <button
                  type="button"
                  data-testid="dana-mic-btn"
                  onTouchStart={onMicTouchStart}
                  onTouchMove={onMicTouchMove}
                  onTouchEnd={onMicTouchEnd}
                  onTouchCancel={onMicTouchEnd}
                  onPointerMove={onMicMove}
                  onPointerUp={onMicRelease}
                  onPointerLeave={onMicRelease}
                  onPointerCancel={onMicRelease}
                  aria-label={isAr ? "التسجيل جارٍ - اترك للإرسال" : "Recording - release to send"}
                  className="dana-mic-btn dana-mic-btn-recording shrink-0"
                  style={{ touchAction: "none" }}
                >
                  <Mic size={20} />
                  <span className="dana-mic-pulse" aria-hidden />
                </button>
                <div
                  className="flex-1 flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/90 border border-red-300/60 text-red-700 text-sm"
                  style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
                >
                  <span className="voice-record-dot" aria-hidden />
                  <span className="tabular-nums font-semibold">{fmtDur(elapsed)}</span>
                  <span className="recording-waveform" aria-hidden>
                    <i></i><i></i><i></i><i></i><i></i>
                  </span>
                  <span className="opacity-70 text-xs flex items-center gap-1">
                    <span aria-hidden>{isAr ? '→' : '←'}</span>
                    {isAr ? "اسحب للإلغاء" : "slide to cancel"}
                  </span>
                </div>
              </div>
            ) : (
              /* IDLE — mic (press-and-hold) + text input + text-send */
              <>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onTouchStart={onMicTouchStart}
                    onTouchMove={onMicTouchMove}
                    onTouchEnd={onMicTouchEnd}
                    onTouchCancel={onMicTouchEnd}
                    onPointerDown={onMicDown}
                    onPointerMove={onMicMove}
                    onPointerUp={onMicRelease}
                    onPointerLeave={onMicRelease}
                    onPointerCancel={onMicRelease}
                    onContextMenu={(e) => e.preventDefault()}
                    disabled={loading || voiceSending}
                    data-testid="dana-mic-btn"
                    aria-label={isAr ? "اضغط باستمرار للتسجيل، اسحب لليسار للإلغاء" : "Press and hold to record, slide left to cancel"}
                    title={isAr ? "اضغط باستمرار للتسجيل، اسحب لليسار للإلغاء" : "Press and hold to record, slide left to cancel"}
                    className="dana-mic-btn shrink-0 select-none"
                    style={{ touchAction: "none", display: "none" }}
                  >
                    <Mic size={20} />
                  </button>
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                    placeholder={t("dana_placeholder")}
                    disabled={loading}
                    data-testid="dana-input"
                    className="flex-1 rounded-full bg-white/90 border border-sage/40 px-4 py-2.5 text-sm text-emerald-deep placeholder:text-emerald-deep/40 focus:outline-none focus:border-emerald-soft transition font-cairo"
                    style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
                  />
                  <button
                    onClick={() => send()}
                    disabled={loading || !input.trim()}
                    data-testid="dana-send-btn"
                    aria-label={t("dana_send")}
                    className="rounded-full bg-emerald-deep text-cream w-11 h-11 flex items-center justify-center hover:bg-emerald-soft transition disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                  >
                    <Send size={16} className="flip-rtl" />
                  </button>
                </div>
                {voiceError && (
                  <div
                    className="mt-2 text-xs px-3 py-2 rounded-lg"
                    role="alert"
                    data-testid="dana-voice-error"
                    style={{
                      background: 'rgba(220, 38, 38, 0.08)',
                      border: '1px solid rgba(220, 38, 38, 0.30)',
                      color: '#7f1d1d',
                      fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans',
                    }}
                  >
                    {voiceError}
                  </div>
                )}
              </>
            )}
          </div>
          <style>{`
            .dana-mic-btn {
              position: relative;
              width: 56px;
              height: 56px;
              border-radius: 9999px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              background: rgba(255,255,255,0.9);
              border: 1px solid rgba(160,192,175,0.55);
              color: #0f2421;
              transition: all .22s ease;
              cursor: pointer;
              /* iOS: prevent the OS from claiming the horizontal drag for scroll,
                 so our touchmove keeps firing for the full slide-to-cancel gesture. */
              touch-action: none !important;
              -webkit-user-select: none;
              user-select: none;
              -webkit-tap-highlight-color: transparent;
            }
            .dana-mic-btn:hover:not(:disabled) {
              background: #FFFFFF;
              border-color: #4EC487;
              transform: translateY(-1px);
              box-shadow: 0 6px 16px -8px rgba(78,196,135,0.5);
            }
            .dana-mic-btn:disabled { opacity: .55; cursor: default; }
            .dana-mic-btn-recording {
              background: linear-gradient(135deg, #7f1d1d, #b91c1c) !important;
              border-color: rgba(255,200,200,0.55) !important;
              color: #FFFFFF !important;
              transform: scale(1.06);
              box-shadow: 0 0 0 6px rgba(239,68,68,0.16), 0 8px 24px -6px rgba(185,28,28,0.5);
            }
            .dana-mic-btn-error {
              background: rgba(220,38,38,0.12) !important;
              border-color: rgba(220,38,38,0.45) !important;
              color: #7f1d1d !important;
            }
            .dana-mic-pulse {
              position: absolute;
              top: 6px;
              right: 6px;
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: #FF3B30;
              box-shadow: 0 0 0 0 rgba(255,59,48,0.7);
              animation: danaMicPulse 1.4s cubic-bezier(0,0,.2,1) infinite;
            }
            @keyframes danaMicPulse {
              0%   { box-shadow: 0 0 0 0 rgba(255,59,48,.55); }
              70%  { box-shadow: 0 0 0 10px rgba(255,59,48,0); }
              100% { box-shadow: 0 0 0 0 rgba(255,59,48,0); }
            }

            /* Voice bubble preview (pending-send state) */
            .dana-voice-bubble {
              display: inline-flex;
              align-items: center;
              gap: 10px;
              padding: 10px 16px;
              border-radius: 9999px;
              background: rgba(78,196,135,0.10);
              border: 1px solid rgba(78,196,135,0.35);
              color: #0f2421;
            }

            /* Voice-message bubble in chat history (STATIC — no animation) */
            .bubble-voice { min-width: 200px; }

            /* Static waveform for chat-history voice messages — fixed heights,
               NO animation. Renders a WhatsApp-like fixed bar graphic. */
            .static-waveform {
              display: inline-flex;
              align-items: center;
              gap: 2px;
              height: 20px;
              flex: 1;
              min-width: 60px;
            }
            .static-waveform i {
              display: block;
              width: 2px;
              background: currentColor;
              opacity: .55;
              border-radius: 2px;
            }
            /* 24 fixed-height bars — hand-tuned pseudo-waveform silhouette. */
            .static-waveform i:nth-child(1)  { height: 5px;  }
            .static-waveform i:nth-child(2)  { height: 9px;  }
            .static-waveform i:nth-child(3)  { height: 14px; }
            .static-waveform i:nth-child(4)  { height: 7px;  }
            .static-waveform i:nth-child(5)  { height: 11px; }
            .static-waveform i:nth-child(6)  { height: 16px; }
            .static-waveform i:nth-child(7)  { height: 9px;  }
            .static-waveform i:nth-child(8)  { height: 5px;  }
            .static-waveform i:nth-child(9)  { height: 13px; }
            .static-waveform i:nth-child(10) { height: 8px;  }
            .static-waveform i:nth-child(11) { height: 12px; }
            .static-waveform i:nth-child(12) { height: 6px;  }
            .static-waveform i:nth-child(13) { height: 10px; }
            .static-waveform i:nth-child(14) { height: 15px; }
            .static-waveform i:nth-child(15) { height: 8px;  }
            .static-waveform i:nth-child(16) { height: 5px;  }
            .static-waveform i:nth-child(17) { height: 11px; }
            .static-waveform i:nth-child(18) { height: 14px; }
            .static-waveform i:nth-child(19) { height: 7px;  }
            .static-waveform i:nth-child(20) { height: 10px; }
            .static-waveform i:nth-child(21) { height: 6px;  }
            .static-waveform i:nth-child(22) { height: 12px; }
            .static-waveform i:nth-child(23) { height: 8px;  }
            .static-waveform i:nth-child(24) { height: 5px;  }
            /* Filled bar highlight while playing (opacity uplift). */
            .static-waveform i.played { opacity: .95; }

            /* Animated waveform for the LIVE recording indicator (5 bars pulsing). */
            .recording-waveform {
              display: inline-flex;
              align-items: center;
              gap: 3px;
              height: 20px;
            }
            .recording-waveform i {
              display: block;
              width: 3px;
              height: 14px;
              background: currentColor;
              border-radius: 2px;
              transform-origin: center;
              animation: waveform-pulse 0.6s ease-in-out infinite alternate;
            }
            .recording-waveform i:nth-child(1) { animation-delay: 0s;    }
            .recording-waveform i:nth-child(2) { animation-delay: 0.15s; }
            .recording-waveform i:nth-child(3) { animation-delay: 0.30s; }
            .recording-waveform i:nth-child(4) { animation-delay: 0.45s; }
            .recording-waveform i:nth-child(5) { animation-delay: 0.60s; }
            /* Emerald variant used in the locked-recording row. */
            .recording-waveform-em i { background: #0f2421; }
            @keyframes waveform-pulse {
              0%   { transform: scaleY(0.30); opacity: .55; }
              100% { transform: scaleY(1.00); opacity: 1;   }
            }

            /* Blinking red dot on the recording row */
            .voice-record-dot {
              display: inline-block;
              width: 10px;
              height: 10px;
              border-radius: 50%;
              background: #ef4444;
              animation: recDotBlink 1s ease-in-out infinite;
            }
            @keyframes recDotBlink {
              0%, 100% { opacity: 1; }
              50%      { opacity: .35; }
            }

            /* Swipe-up-to-lock floating chip anchored above the mic button.
               Absolutely positioned so it doesn't push layout around.
               Uses inset-inline-start so the chip stays anchored on the
               MIC-BUTTON side of the row in both LTR (left) and RTL (right). */
            .dana-lock-hint {
              position: absolute;
              bottom: calc(100% + 10px);
              inset-inline-start: 6px;
              display: inline-flex;
              flex-direction: column;
              align-items: center;
              gap: 4px;
              padding: 10px 12px;
              border-radius: 18px;
              background: rgba(15, 36, 33, 0.94);
              color: #F5E9C8;
              border: 1px solid rgba(212, 175, 55, 0.45);
              box-shadow: 0 10px 24px -10px rgba(15,36,33,.6);
              pointer-events: none;
              z-index: 5;
              animation: lockHintFloat 1.4s ease-in-out infinite;
              white-space: nowrap;
            }
            .dana-lock-hint-label {
              font-size: 11px;
              font-weight: 600;
              letter-spacing: 0.02em;
              opacity: 0.95;
            }
            .dana-lock-hint-arrow {
              display: block;
              width: 0;
              height: 0;
              border-left: 5px solid transparent;
              border-right: 5px solid transparent;
              border-bottom: 7px solid #F5E9C8;
              opacity: .9;
            }
            @keyframes lockHintFloat {
              0%, 100% { transform: translateY(0); opacity: .95; }
              50%      { transform: translateY(-4px); opacity: 1; }
            }
          `}</style>
        </div>
        </>
      )}
    </>
  );
}

// ── VoiceMessageBubble ─────────────────────────────────────────────────
// STATIC audio player rendered inside a chat message bubble. WhatsApp-style:
// play/pause button + fixed 24-bar waveform silhouette (NEVER animated) +
// duration text that flips to elapsed time while playing.
function VoiceMessageBubble({ audioUrl, duration, fmtDur }) {
  const [playing, setPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0); // 0..1
  const [currentTime, setCurrentTime] = React.useState(0);
  const audioRef = React.useRef(null);

  React.useEffect(() => {
    // Pause + reset if the URL changes (or component unmounts).
    return () => {
      try { audioRef.current?.pause(); } catch (_) {}
    };
  }, [audioUrl]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el || !audioUrl) return;
    if (playing) {
      el.pause();
    } else {
      el.play().catch(() => {});
    }
  };

  const onTimeUpdate = () => {
    const el = audioRef.current;
    if (!el || !el.duration || !isFinite(el.duration)) return;
    setCurrentTime(el.currentTime);
    setProgress(el.currentTime / el.duration);
  };
  const onEnded = () => {
    setPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  };

  // Which of the 24 bars are "played" — used to bump their opacity subtly.
  const playedBars = Math.round(progress * 24);
  const bars = Array.from({ length: 24 }, (_, i) => (
    <i key={i} className={i < playedBars ? "played" : ""} />
  ));

  const label = playing ? fmtDur(Math.round(currentTime)) : fmtDur(duration);

  return (
    <span className="flex items-center gap-2 min-w-0">
      <button
        type="button"
        onClick={toggle}
        disabled={!audioUrl}
        aria-label={playing ? "Pause" : "Play"}
        className="rounded-full w-8 h-8 flex items-center justify-center bg-black/10 hover:bg-black/15 transition disabled:opacity-40 shrink-0"
      >
        {playing ? <Pause size={14} /> : <Play size={14} />}
      </button>
      <span className="static-waveform" aria-hidden>{bars}</span>
      <span className="tabular-nums opacity-90 text-xs shrink-0">{label}</span>
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={onEnded}
          onTimeUpdate={onTimeUpdate}
        />
      )}
    </span>
  );
}
